import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import './VelocityButton.scss';

type VelocityButtonVariant = "primary" | "secondary" | "tertiary" | "destructive" | "compact";

interface VelocityButtonProps {
  variant?: VelocityButtonVariant;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: (event?: any) => void;
  href?: string;
  target?: string;
  className?: string;
  showArrow?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const CustomArrowIcon = ({ hover, disabled, variant, pressed }: { hover: boolean; disabled?: boolean; variant: VelocityButtonVariant; pressed?: boolean }) => {
  const getRotation = () => {
    if (variant === "primary" || variant === "destructive") {
      return hover && !disabled ? "rotate(-45deg)" : "rotate(0deg)";
    }
    return "rotate(0deg)";
  };

  const getArrowColor = () => {
    if (variant === "tertiary" && pressed) {
      return '#6366f1';
    }
    if (variant === "secondary") {
      return "white";
    }
    return '#432568';
  };

  const showCircle = variant === "primary" || variant === "destructive";

  if (showCircle) {
    return (
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease",
          transform: getRotation(),
          opacity: disabled ? 0.5 : 1,
          marginLeft: 12,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{ color: '#432568' }}
        >
          <path
            d="M2 6L10 6M10 6L7 3M10 6L7 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 12 12"
      fill="none"
      style={{ 
        color: getArrowColor(),
        transition: "opacity 0.2s ease",
        opacity: disabled ? 0.5 : 1,
        marginLeft: 12,
      }}
    >
      <path
        d="M2 6L10 6M10 6L7 3M10 6L7 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const VelocityButton = ({
  variant = "primary",
  children,
  disabled,
  onClick,
  href,
  target,
  className = "",
  showArrow = true,
  onMouseEnter,
  onMouseLeave,
}: VelocityButtonProps) => {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  const shouldShowArrow = () => {
    if (!showArrow) return false;
    
    if (variant === "primary" || variant === "destructive") {
      return true; // Always show, rotates on hover
    }
    if (variant === "secondary" || variant === "tertiary") {
      return hover && !disabled; // Show only on hover, no rotation
    }
    if (variant === "compact" && hover && !disabled) {
      return true;
    }
    return false;
  };

  const handleMouseEnter = () => {
    setHover(true);
    if (onMouseEnter) onMouseEnter();
  };

  const handleMouseLeave = () => {
    setHover(false);
    setPressed(false);
    if (onMouseLeave) onMouseLeave();
  };

  const buttonProps = {
    className: `velocity-btn velocity-btn--${variant} ${pressed ? 'pressed' : ''} ${hover ? 'hover' : ''} ${className}`,
    disabled,
    onClick: disabled ? (e: any) => { e.preventDefault(); e.stopPropagation(); } : onClick,
    href: disabled ? undefined : href,
    target: disabled ? undefined : target,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseDown: () => setPressed(true),
    onMouseUp: () => setPressed(false),
  };

  return (
    <Button {...buttonProps}>
      {children}
      {shouldShowArrow() && <CustomArrowIcon hover={hover} disabled={disabled || false} variant={variant} pressed={pressed} />}
    </Button>
  );
};